//@ts-nocheck
import { LazyQueryHookOptions, useLazyQuery, gql } from '@apollo/client';
import { ErrorMessage, FieldProps } from 'formik';
import * as React from 'react';
import { useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Box } from '../';
import { StyledSystemProps } from '../../../theme/StyledSystemProps';
import { Label, Span } from '../../../typography';
import {
  getSelectOptionsFromQuery,
  SelectOptionsFromQueryOptionsType,
  selectScrollHandlerForQuery,
} from '../../../utils/selectHelpers';

type Option = { label: string; value: boolean | string | number | null; otherField?: any; field?: any };

type SelectQueryOptionsType = LazyQueryHookOptions & {
  searchTextField?: string;
  resultItemOptions?: SelectOptionsFromQueryOptionsType;
  skip?: boolean;
  query?: any;
};

type CustomProps = StyledSystemProps & {
  color?: string;
  disableChange?: boolean;
  hideDropdown?: boolean;
  hideMultiOptionRemove?: boolean;
  inputValue?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  loading?: boolean;
  label?: string;
  name: string;
  options?: Array<Option>;
  placeholder?: string;
  readOnly?: boolean;
  setSearchTerm?: Function;
  value?: any;
  changeHandler?: (option: { value: any }, allOptions?: Option[]) => void;
  scrollHandler?: () => void;
  query?: SelectQueryOptionsType;
  hideErrorMessage?: boolean;
  staticOptions?: Array<Option>;
};

const StyledSelect = styled(Select)`
  & .react-select__control {
    min-height: 40px;
  }
  & .react-select__menu .react-select__menu-list .react-select__option--is-selected {
    background-color: ${themeGet('colors.putty', '#EEE')};
  }
  & .react-select__control--is-focused {
    border: solid 2px ${themeGet('colors.putty', '#EEE')};
    box-shadow: none;
  }
  & .react-select__control:hover {
    border: solid 2px ${themeGet('colors.putty', '#EEE')};
    box-shadow: none;
  }
`;

const combineQueryVariables = (variables: any, querySearchTextField: string | undefined, searchText: string) => {
  if (variables === undefined) variables = {};
  if (!querySearchTextField) return variables;

  const querySearchTextFields: string[] = querySearchTextField.split('.');

  var obj = variables;
  querySearchTextFields.forEach((v, i, a) => {
    if (i < a.length - 1) {
      if (obj[v] === undefined) obj[v] = {};
      obj = obj[v];
    } else if (i === a.length - 1) {
      if (!searchText) {
        delete obj[v];
      } else {
        obj[v] = searchText;
      }
    }
  });
  return variables;
};

export interface IStyledFormSelect {
  getOptions: () => Option[];
}

const StyledFormSelect: React.ForwardRefRenderFunction<IStyledFormSelect, Omit<FieldProps, 'meta'> & CustomProps> = (
  props: any,
  ref: any
) => {
  const [timer, setTimer] = useState(0);
  const [options, setOptions] = useState<Option[]>([]);
  const [querySearchText, setQuerySearchText] = useState('');
  const [queryResultAllEmptySearchText, setQueryResultAllEmptySearchText] = useState(false);

  // const getOptions = () => {
  //   return props.options ? props.options : options;
  // };

  // React.useImperativeHandle(ref, () => ({
  //   getOptions,
  // }));

  const {
    query: queryQuery,
    resultItemOptions: queryResultItemOptions,
    searchTextField: querySearchTextField,
    skip: skipQuery,
    variables: _queryVariables,
    ...queryOptions
  } = props.query || {};
  const { loading, ...restProps } = props;

  const [queryVariablesOriginal, setQueryVariablesOriginal] = useState<any>(undefined);
  const [queryVariables, setQueryVariables] = useState<any>(undefined);

  const lazyQuery = useLazyQuery(
    queryQuery ||
      gql`
        query {
          __typename
        }
      `,
    {
      ...queryOptions,
      variables: queryVariables,
      skip: !queryQuery, // Skip the query if queryQuery is undefined
    }
  );
  const [queryFetch, query] = !skipQuery && !!queryQuery ? lazyQuery : [undefined, undefined];

  React.useEffect(() => {
    if (query && !!queryFetch) {
      //console.log(`${props.name}: _queryVariables`);
      if (JSON.stringify(_queryVariables || {}) !== JSON.stringify(queryVariablesOriginal || {})) {
        //console.log(`${props.name}: _queryVariables - changed`);
        setQueryVariablesOriginal(_queryVariables);
        setQuerySearchText('');
        setQueryVariables(combineQueryVariables(_queryVariables, querySearchTextField, querySearchText));
      }
    }
  }, [_queryVariables]);

  React.useEffect(() => {
    if (query && !!queryFetch) {
      //console.log(`${props.name}: querySearchText`);
      setQueryVariables(combineQueryVariables(_queryVariables, querySearchTextField, querySearchText));
    }
  }, [querySearchText]);

  React.useEffect(() => {
    if (query && !!queryFetch) {
      //console.log(`${props.name}: queryVariables`);
      queryFetch(queryVariables);
    }
  }, [queryVariables]);

  React.useEffect(() => {
    if (query && !!queryFetch) {
      //console.log(`${props.name}: query.data`);
      if (!!query.data) {
        //console.log(`${props.name}: query.data - complete`);
        const result = getSelectOptionsFromQuery(query, queryResultItemOptions);
        const finalOptions = props.staticOptions ? [...props.staticOptions, ...result.values] : result.values;
        setOptions(finalOptions);
        if (querySearchText === '') {
          setQueryResultAllEmptySearchText(result.values.length === result.count);
        }
      } else {
        //console.log(`${props.name}: query.data - cleared`);
        setOptions(props.staticOptions || []);
        setQueryResultAllEmptySearchText(false);
      }
    }
  }, [query?.data, props.staticOptions]);

  const onMenuScrollToBottom =
    props.scrollHandler !== undefined
      ? props.scrollHandler
      : !!query && !skipQuery
      ? async () => selectScrollHandlerForQuery(query, queryVariables, queryResultItemOptions)
      : undefined;

  const getValue = (options: Option[] | undefined, value: any): Option[] | Option | null => {
    if (value === undefined || value === null || options === undefined) return null;
    if (Array.isArray(value)) {
      if (value.length === 0) return [];
      if (typeof value[0] === 'object') {
        if (value[0]['label'] !== undefined && value[0]['value'] !== undefined) return value;
        if (value[0]['value'] !== undefined) {
          return value
            .map((v: any) => {
              const o = options.find(o => o.value === v.value);
              if (o !== undefined) return o;
              return { label: '', value: '' };
            })
            .filter(v => v.value !== '');
        }
      } else if (typeof value[0] === 'string') {
        return value
          .map((v: string) => {
            const o = options.find(o => o.value !== undefined && o.value !== null && o.value.toString() === v);
            if (o !== undefined) return o;
            return { label: '', value: '' };
          })
          .filter(v => v.value !== '');
      } else if (typeof value[0] === 'number') {
        return value
          .map((v: number) => {
            const o = options.find(o => o.value === v);
            if (o !== undefined) return o;
            return { label: '', value: '' };
          })
          .filter(v => v.value !== '');
      }
    } else {
      if (typeof value === 'object' && 'value' in value) {
        const v = options.find(x => x.value === value['value']);
        return !!v ? v : null;
      } else {
        const v = options.find(x => x.value === value);
        return !!v ? v : null;
      }
    }
    return null;
  };

  const customStyles = {
    ...((props.isDisabled || props.readOnly || props.hideMultiOptionRemove) && {
      multiValueRemove: (style: any, state: any) => {
        return {
          ...style,
          visibility: 'hidden',
          width: '1px',
        };
      },
    }),
    ...(props.color && {
      singleValue: (style: any, state: any) => {
        return {
          ...style,
          color: props.color,
        };
      },
    }),
  };

  var customComponents = undefined;
  if (props.hideDropdown) {
    customComponents = {
      Menu: () => null,
      MenuList: () => null,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
    };
  }

  return (
    <Box {...restProps}>
      <Label htmlFor={props.field.name}>{props.label}</Label>
      <StyledSelect
        classNamePrefix="react-select"
        inputValue={props.inputValue}
        isClearable={props.isClearable}
        isDisabled={props.isDisabled || props.readOnly}
        isMulti={props.isMulti}
        name={props.field.name}
        options={props.options ? props.options : options}
        isLoading={!!query && !skipQuery ? query.loading : loading}
        onBlur={props.field.onBlur}
        onInputChange={
          props.setSearchTerm === undefined &&
          (!querySearchTextField || (!!querySearchTextField && queryResultAllEmptySearchText))
            ? undefined
            : (data: string, event: any) => {
                if (event.action === 'input-change') {
                  clearTimeout(timer);
                  var t: any = setTimeout(() => {
                    if (!!querySearchTextField) {
                      setQuerySearchText(data);
                    } else if (props.setSearchTerm) {
                      props.setSearchTerm(data);
                    }
                  }, 1000);
                  setTimer(t);
                }
              }
        }
        onChange={(option: any) => {
          if (!(props.disableChange === true)) {
            const currentValue: any = props.form.values[props.field.name];
            const allOptions = props.options ? props.options : options;
            if (currentValue && option && Array.isArray(currentValue) && !Array.isArray(option)) {
              props.form.setFieldValue(props.field.name, [option]);
            } else {
              props.form.setFieldValue(props.field.name, option);
            }
            props.changeHandler && props.changeHandler(option, allOptions);
          }
        }}
        onMenuScrollToBottom={onMenuScrollToBottom}
        placeholder={props.placeholder}
        value={
          !!query
            ? options.length === 0
              ? null
              : getValue(options, props.field.value || props.value)
            : getValue(props.options, props.field.value || props.value)
        }
        loadingMessage={() => 'loading...'}
        noOptionsMessage={() => 'No options found...'}
        styles={customStyles}
        components={customComponents}
        backspaceRemovesValue={props.hideMultiOptionRemove === undefined ? undefined : !props.hideMultiOptionRemove}
      />
      {!props.hideErrorMessage && (
        <ErrorMessage name={props.field.name}>
          {msg => {
            return (
              <Span mt={2} color="error" textAlign="right">
                {msg}
              </Span>
            );
          }}
        </ErrorMessage>
      )}
    </Box>
  );
};

export const ForwardRefStyledFormSelect = React.forwardRef(StyledFormSelect);

export default StyledFormSelect;

StyledFormSelect.defaultProps = {
  options: undefined,
  mb: 3,
  flex: 1,
  minWidth: '150px',
  width: '100%',
};

ForwardRefStyledFormSelect.defaultProps = StyledFormSelect.defaultProps;
